import { useState } from 'react';
import {
    StatusContainer,
    isActivePreApprovalSubmission,
    ApplicationSubmitToDecisionGate,
} from '@servicetitan/financing-utils-shared';
import { LoanDetails } from './LoanDetails';
import { SoftAppDetails } from './SoftAppDetails';
import ApprovedIllustration from '../../common/illustrations/approved-decision.svg';
import {
    SetupDataStore,
    useProviderConfig,
    PrequalificationResponseStore,
    getSoftPrequalContinuationId,
} from '@servicetitan/financing-workflow-shared';
import { Button, Flex } from '@servicetitan/anvil2';
import { useCreateAccount } from './hooks/useCreateAccount';
import { AcceptedOffer } from './AcceptedOffer';
import { OfferAcceptedStore } from '../stores/OfferAcceptedStore';
export interface ApprovedProps {
    onViewOfferDetails?: () => void;
    isSecondLookApproval?: boolean;
    cameFromLoanTaskView?: boolean;
}

export const Approved = ({
    isSecondLookApproval = false,
    cameFromLoanTaskView = false,
    onViewOfferDetails,
}: ApprovedProps) => {
    const [offerAccepted, setOfferAccepted] = useState(false);
    const { setOfferAccepted: setGlobalOfferAccepted } = OfferAcceptedStore.useStore();

    const {
        continueToHardAppAfterSoft,
        softAppApprovedHeadline,
        softAppApprovedMessage,
        showSoftAppMessageForPrequalification,
        customerCanAcceptOffer,
        approvedMessage,
        useFinancingPrequalSubmission,
    } = useProviderConfig();
    const { setupData } = SetupDataStore.useStore();
    const { prequalificationResponse } = PrequalificationResponseStore.useStore();

    const hasFinancingSoftPrequal =
        useFinancingPrequalSubmission === true &&
        getSoftPrequalContinuationId(prequalificationResponse) != null &&
        !setupData?.hasTransitionedToHardApp;

    // The loan task view is only reachable from an already-approved hard loan, and its setup data
    // carries no loanId, so the heuristics below would otherwise read it as a soft app.
    const isSoftApp = cameFromLoanTaskView
        ? false
        : isActivePreApprovalSubmission(setupData)
          ? false
          : hasFinancingSoftPrequal
            ? true
            : continueToHardAppAfterSoft && !setupData?.prequalificationId && !setupData?.loanId
              ? true
              : showSoftAppMessageForPrequalification && setupData?.isApplyingForPrequalification;

    const headline = isSoftApp
        ? softAppApprovedHeadline
        : 'Congratulations! Your loan application was approved.';
    const message = isSoftApp
        ? softAppApprovedMessage
        : (approvedMessage ??
          'Review your loan details and continue to sign and accept the offer.');

    const { createAccount, isLoading } = useCreateAccount({
        onSuccess: () => {
            setOfferAccepted(true);
            setGlobalOfferAccepted(true);
        },
    });

    const showAcceptOfferButtons = customerCanAcceptOffer && !isSoftApp && !isSecondLookApproval;

    // Show AcceptedOffer component after successful account creation
    if (offerAccepted && customerCanAcceptOffer) {
        return (
            <AcceptedOffer
                cameFromLoanTaskView={cameFromLoanTaskView}
                onViewOfferDetails={onViewOfferDetails}
            />
        );
    }

    return (
        <ApplicationSubmitToDecisionGate>
            <StatusContainer
                headline={headline ?? ''}
                message={message}
                image={ApprovedIllustration}
            >
                {isSoftApp ? <SoftAppDetails /> : <LoanDetails />}
                {showAcceptOfferButtons && (
                    <Flex direction="column" gap="4" style={{ width: '100%', marginTop: '1rem' }}>
                        <Flex direction="column" gap="2">
                            <Button
                                appearance="primary"
                                onClick={createAccount}
                                loading={isLoading}
                                disabled={isLoading}
                                style={{ width: '100%' }}
                            >
                                Accept Offer
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </StatusContainer>
        </ApplicationSubmitToDecisionGate>
    );
};
